import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CountIcon from '@mui/icons-material/BurstModeOutlined';
import DownloadIcon from '@mui/icons-material/GetApp';
import FootprintIcon from '@mui/icons-material/SelectAll';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ListSubheaderUI from '@mui/material/ListSubheader';
import MUIListItem from '@mui/material/ListItem';
import MUIToggleButton from '@mui/material/ToggleButton';
import PropTypes from 'prop-types';
import React from 'react';
import RightArrowIcon from '@mui/icons-material/ArrowForward';
import ShareIcon from '@mui/icons-material/Share';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

const BUTTON_SIZE = theme => theme.spacing(3.75); // NOTE: Uses non-standard quarter-multiple for better design fit
export const THUMBNAIL_DIMENSIONS = 140;
export const THUMBNAIL_DIMENSIONS_V2 = 115;
export const GROUP_ITEM_DIMENSIONS = 115;
export const LIST_ITEM_HEIGHT = 8 * 4 + THUMBNAIL_DIMENSIONS;
export const LIST_GROUP_ITEM_HEIGHT = 8 * 3 + GROUP_ITEM_DIMENSIONS;
export const CARD_LAYOUT_STYLES = {
  display: 'grid',
  gridTemplateColumns: 'auto auto 1fr auto',
  gridTemplateRows: 'auto 1fr auto',
  gridTemplateAreas: `
    "control thumb title action"
    "control thumb content content"
    "control thumb view view"
  `,
  gap: theme => `${theme.spacing(0.5)} 0`,
};

export const StyledBadge = props => (
  <Badge
    color="primary"
    max={99}
    sx={{
      '& .MuiBadge-badge': {
        fontSize: 11,
        minWidth: 18,
        height: 18,
      },
    }}
    {...props}
  />
);

export const ItemTitle = props => (
  <Typography variant="subtitle1" display="block" {...props} />
);

export const GroupTitle = props => (
  <Typography variant="subtitle2" display="inline" {...props} />
);

export const ListSubheader = styled(ListSubheaderUI)(({theme}) => ({
  backgroundColor: theme.palette.common.white,
  lineHeight: 'inherit',
  zIndex: 20,
}));

export const ItemId = props => (
  <Typography variant="body2" fontFamily="monospace" fontSize={13} {...props} />
);

export const ItemCountIcon = styled(CountIcon)(({theme}) => ({
  marginRight: theme.spacing(0.5),
}));

export const ItemsCount = ({showIcon = true, selected, total, suffix}) => {
  const {t} = useTranslation();

  return (
    <Tooltip
      title={
        selected && selected > 0
          ? t('itemsPanel.tooltipSelected', {selected, total})
          : ''
      }
    >
      <Box display="inline-flex" alignItems="center" mr={1} color="grey.600">
        {showIcon && <ItemCountIcon color="inherit" fontSize="small" />}
        <Typography
          color="textSecondary"
          variant="caption"
          display="initial"
          data-qe="item-count"
        >
          <Typography color="primary" variant="caption" display="inline">
            {selected > 0 && selected}
          </Typography>
          <span>{selected > 0 ? '/' : ''}</span>
          {total || 0} {suffix ? suffix : ''}
        </Typography>
      </Box>
    </Tooltip>
  );
};

ItemsCount.propTypes = {
  showIcon: PropTypes.bool,
  selected: PropTypes.number,
  total: PropTypes.number,
  suffix: PropTypes.string,
};

// If list item is selected and spacebar is pressed
// Prevent toggling the item, since we use spacebar
// in the Explorer map to temporarily hide imagery
export const overrideSpacebar = event => {
  if (event.keyCode === 32) {
    event.preventDefault();
  }
};

export const ListItem = React.forwardRef(
  ({isDragging, isComparing, sx, ...props}, ref) => (
    <MUIListItem
      ref={ref}
      sx={{
        cursor:
          isComparing && !isDragging
            ? 'grab'
            : isDragging
              ? 'grabbing'
              : 'pointer',
        ...sx,
      }}
      ContainerProps={{
        sx: {
          transform: 'translate3d(0, 0, 0)',
          backgroundColor: theme => theme.palette.background.paper,
        },
      }}
      ContainerComponent="div"
      onKeyUp={overrideSpacebar}
      {...props}
    />
  )
);
ListItem.propTypes = {
  isDragging: PropTypes.bool,
  isComparing: PropTypes.bool,
  sx: PropTypes.object,
};

export const LoadMoreButton = ({children, ...props}) => {
  const {t} = useTranslation();

  return (
    <Box m={2}>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        {...props}
      >
        {children ? children : t('itemsPanel.buttonLoadMore')}
      </Button>
    </Box>
  );
};
LoadMoreButton.propTypes = {
  children: PropTypes.node,
};

export const RequestAccessButton = props => (
  <Button
    fullWidth
    variant="contained"
    color="primary"
    size="small"
    sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: 0,
      backgroundColor: 'grey.700',
      padding: 0.5,
    }}
    {...props}
  />
);

export const ToggleButton = React.forwardRef(
  ({disabled, onClick, ...props}, ref) => (
    <MUIToggleButton
      sx={{
        height: BUTTON_SIZE,
        width: BUTTON_SIZE,
        color: 'grey.700',
        borderColor: 'grey.700',
        '&.Mui-selected': {
          borderColor: theme => theme.palette.primary.main,
        },
        '&.Mui-disabled': {
          backgroundColor: 'grey.200',
          color: 'grey.400',
          borderColor: 'grey.200',
        },
      }}
      color="primary"
      component={disabled ? 'div' : undefined}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      ref={ref}
      {...props}
    />
  )
);

ToggleButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

export const ViewToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton data-qe="view-on-map-button" ref={ref} {...props}>
    <VisibilityIcon fontSize="small" />
  </ToggleButton>
));

ViewToggleButton.propTypes = {
  selected: PropTypes.bool,
};

export const EyeBadgeIcon = props => {
  return (
    <Box
      data-qe="eye-badge-icon"
      sx={{
        position: 'absolute',
        top: 2,
        right: 2,
        width: 25,
        height: 25,
        color: props.isViewSelected ? 'background.paper' : 'grey.500',
        bgcolor: props.isViewSelected ? 'primary.main' : 'grey.300',
        borderRadius: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <VisibilityIcon fontSize="small" />
    </Box>
  );
};

EyeBadgeIcon.propTypes = {
  isViewSelected: PropTypes.bool,
};

export const OrderToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton data-qe="add-to-order-button" ref={ref} {...props}>
    <AddShoppingCartOutlinedIcon fontSize="small" />
  </ToggleButton>
));

export const InfoToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <InfoOutlinedIcon fontSize="small" />
  </ToggleButton>
));

export const DownloadToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <DownloadIcon fontSize="small" />
  </ToggleButton>
));

export const ShareToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <ShareIcon fontSize="small" />
  </ToggleButton>
));

export const FootprintToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <FootprintIcon fontSize="small" />
  </ToggleButton>
));

export const LinksToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <LinkIcon fontSize="small" />
  </ToggleButton>
));

export const ViewItemsButton = ({itemCount, ...props}) => {
  const {t} = useTranslation();

  return (
    <Button
      size="small"
      variant="contained"
      color="primary"
      endIcon={<RightArrowIcon />}
      sx={{
        height: BUTTON_SIZE,
        py: 0,
        px: 1,
        justifyContent: 'space-between',
        minWidth: 100,
      }}
      aria-label={t(`resultItem.viewItems`, {
        count: itemCount,
      })}
      {...props}
    >
      <span data-qe="item-count">
        {t('resultItem.itemCount', {count: itemCount})}
      </span>
    </Button>
  );
};

ViewItemsButton.propTypes = {
  itemCount: PropTypes.number.isRequired,
};
